/**
 * Shared read/show markdown renderer (FRONTEND-FOLLOWUP-CONTRACT §D).
 *
 * The single themed surface for rendering the rich markdown that the entities
 * carry (artifact bodies, decision rationale, package `body_markdown`, context
 * descriptions, doc bodies, the Documentation reading pane, …). Built on
 * `react-markdown` + `remark-gfm`, themed entirely with shadcn CSS-variable
 * utility classes (NO `@tailwindcss/typography` — the tailwind config ships no
 * plugins, so `prose` is unavailable; each element is styled explicitly).
 *
 * react-markdown sanitizes by default (raw HTML is dropped unless `rehype-raw`
 * is added — we deliberately do NOT add it), so this is XSS-safe even on
 * untrusted, AI-authored markdown.
 *
 * Relationship to the existing renderers: `components/app/markdown.ts`
 * (`renderMarkdown`) + `MarkdownEditor` are the *editor preview* path and stay
 * as-is. This `<Markdown>` is the *read* path used by the show pages and the
 * `/docs` reader.
 */
import * as React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

export interface MarkdownProps {
  /** The markdown body. `null`/`undefined`/blank renders a muted em-dash. */
  source: string | null | undefined;
  className?: string;
}

/**
 * react-markdown v9 removed the `inline` prop from the `code` renderer, so a
 * code node is "inline" iff it is not the direct child of a `<pre>` — which the
 * library signals by *not* attaching a `language-*` class and by the absence of
 * a trailing newline. We render block code via `pre` (which wraps the styled
 * `code`) and keep `code` itself inline-styled; a fenced block's `code` child
 * carries a `language-` class, so we strip its inline chrome in that case.
 */
const COMPONENTS: Components = {
  // §9 real heading hierarchy + section rhythm so docs read as sections, not a
  // mashed wall of text. h1 gets an underline rule to anchor each section.
  h1: (p) => (
    <h1
      className="mt-8 mb-3 border-b border-border pb-2 text-2xl font-semibold tracking-tight first:mt-0"
      {...p}
    />
  ),
  h2: (p) => (
    <h2 className="mt-6 mb-2 text-xl font-semibold tracking-tight" {...p} />
  ),
  h3: (p) => <h3 className="mt-4 mb-1.5 text-lg font-semibold" {...p} />,
  h4: (p) => <h4 className="mt-3 mb-1 text-base font-semibold" {...p} />,
  // Block rhythm lives on each element now (the wrapper dropped `space-y-*` so
  // headings keep their own `mt-*`). `[&:first-child]:mt-0` stops a leading
  // block from pushing the article body down under the page header.
  p: (p) => <p className="my-4 leading-7 [&:first-child]:mt-0" {...p} />,
  ul: (p) => (
    <ul className="my-4 ml-6 list-disc space-y-1.5 [&:first-child]:mt-0" {...p} />
  ),
  ol: (p) => (
    <ol
      className="my-4 ml-6 list-decimal space-y-1.5 [&:first-child]:mt-0"
      {...p}
    />
  ),
  li: (p) => <li className="leading-7" {...p} />,
  a: (p) => (
    <a
      className="font-medium text-primary underline underline-offset-2"
      target="_blank"
      rel="noreferrer noopener"
      {...p}
    />
  ),
  code: ({ className, ...p }) => {
    // Fenced blocks carry a `language-*` class (and live inside our themed
    // `pre`); inline code does not. Strip the inline chrome for block code so
    // the `pre` background owns the styling.
    const isBlock = typeof className === "string" && className.includes("language-");
    return (
      <code
        className={cn(
          isBlock
            ? "font-mono"
            : "rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]",
          className,
        )}
        {...p}
      />
    );
  },
  pre: (p) => (
    <pre
      className="my-4 overflow-x-auto rounded-md border border-border bg-muted p-3 text-xs"
      {...p}
    />
  ),
  blockquote: (p) => (
    <blockquote
      className="my-4 border-l-2 border-border pl-4 italic text-muted-foreground"
      {...p}
    />
  ),
  table: (p) => (
    <div className="my-4 overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm" {...p} />
    </div>
  ),
  thead: (p) => <thead className="bg-muted/50" {...p} />,
  tbody: (p) => <tbody className="divide-y divide-border" {...p} />,
  tr: (p) => <tr className="border-b border-border last:border-0" {...p} />,
  th: (p) => <th className="px-3 py-2 text-left font-medium" {...p} />,
  td: (p) => <td className="px-3 py-2 align-top" {...p} />,
  hr: () => <hr className="my-6 border-border" />,
  img: (p) => <img className="max-w-full rounded-md" {...p} />,
};

/**
 * Render a markdown string with the shadcn-minimal reading theme. Falls back to
 * a muted em-dash for empty/blank content so callers can pass a field straight
 * through without a null guard.
 */
export function Markdown({ source, className }: MarkdownProps) {
  if (!source || !source.trim()) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    // NOTE: no `space-y-*` here — the per-element COMPONENTS margins own the
    // vertical rhythm (h1 `mt-8`, h2 `mt-6`, …). A wrapper `space-y` selector
    // (`> * + *`) outranks those `mt-*` classes and flattens every heading to a
    // uniform gap, which is exactly the "mashed/slipped" section feel we're
    // fixing (§9). Let the element margins read as real sections instead.
    <div
      className={cn(
        "text-sm leading-7 text-foreground [overflow-wrap:anywhere]",
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {source}
      </ReactMarkdown>
    </div>
  );
}
